import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Building2, DollarSign, Hash, TrendingUp, ChevronDown, Image as ImageIcon, Upload, Trash2 } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { createStockItem, StockItem } from '../lib/stockItems';
import { supabase } from '../lib/supabase';
import { getSuppliers, createSupplier, Customer } from '../lib/customers';
import { getStockCategories, createStockCategory, StockCategory } from '../lib/stockCategories';
import { ProductPhotoService } from '../services/productPhotoService';
import VariantsEditor, { VariantRow } from './VariantsEditor';
import { createProductGroupWithVariants, convertItemToVariantGroup, ProductOption } from '../lib/productGroups';
import MediaLibraryPicker from './MediaLibraryPicker';
import { MediaItem } from '../lib/mediaLibrary';
import { defaultRentalAgreement } from '../lib/rentals';
import {
  createStockItemAdditionalTax,
  deleteStockItemAdditionalTax,
  getStockItemAdditionalTaxes,
  StockItemAdditionalTax,
} from '../lib/tax-rates';

const STOCK_UNITS = {
  quantity: [
    { value: 'unit', label: 'Unit' },
    { value: 'box', label: 'Box' },
    { value: 'pack', label: 'Pack' },
    { value: 'set', label: 'Set' }
  ],
  length: [
    { value: 'ft', label: 'Foot (ft)' },
    { value: 'm', label: 'Meter (m)' }
  ],
  volume: [
    { value: 'gal', label: 'Gallon (gal)' },
    { value: 'l', label: 'Liter (L)' }
  ]
};

interface NewStockItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedSupplierId?: string | null;
  isOneTime?: boolean;
  onItemCreated?: (itemId: string) => void;
  /** When provided, the modal opens in edit mode for this existing item. */
  editItem?: StockItem | null;
  /** Current photo URL of the item being edited (if any). */
  editPhotoUrl?: string | null;
}

export default function NewStockItemModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedSupplierId,
  isOneTime = false,
  onItemCreated,
  editItem = null,
  editPhotoUrl = null
}: NewStockItemModalProps) {
  const isEditMode = !!editItem;
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);
  const [isSelectingSupplier, setIsSelectingSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    acquisition_cost: '0',
    price: '0',
    deposit: '',
    depositLabel: '',
    sku: '',
    unit: 'unit',
    quantity: '0',
    reorder_point: '0',
    max_quantity: '100',
    is_one_time: false,
    track_inventory: true,
    allow_oversell: false,
    tax_exempt: false,
    show_online: true,
    rental_enabled: false,
    rental_price: '',
    rental_interval: 'month' as 'month' | 'week',
    rentalAgreement: '',
    restock_returns: false
  });
  const [extraTaxes, setExtraTaxes] = useState<StockItemAdditionalTax[]>([]);
  const [addingTax, setAddingTax] = useState(false);
  const [taxName, setTaxName] = useState('');
  const [taxRateInput, setTaxRateInput] = useState('');
  const [images, setImages] = useState<{ id: string; url: string; preview: string }[]>([]);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  // Variants (optional "this product has options")
  const [hasVariants, setHasVariants] = useState(false);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [variants, setVariants] = useState<VariantRow[]>([]);

  const handleMediaSelected = (selected: MediaItem[]) => {
    const mapped = selected.map(m => ({ id: m.id, url: m.url, preview: m.url }));
    // Simple items have a single photo; variant products keep a gallery.
    setImages(hasVariants ? mapped : mapped.slice(0, 1));
  };

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCategories();
      loadSuppliers();
      if (editItem) {
        // ===== Edit mode: prefill from the existing item =====
        const isOT = editItem.is_one_time === true;
        setFormData({
          name: editItem.name,
          description: editItem.description || '',
          acquisition_cost: editItem.acquisition_cost.toString(),
          price: editItem.price.toString(),
          deposit: editItem.deposit_cents ? (editItem.deposit_cents / 100).toString() : '',
          depositLabel: editItem.deposit_label || '',
          sku: editItem.sku || '',
          unit: editItem.unit,
          quantity: editItem.quantity.toString(),
          reorder_point: isOT ? '' : (editItem.reorder_point ?? 0).toString(),
          max_quantity: isOT ? '' : (editItem.max_quantity ?? 100).toString(),
          is_one_time: isOT,
          track_inventory: editItem.track_inventory !== false,
          allow_oversell: editItem.allow_oversell === true,
          tax_exempt: editItem.tax_exempt === true,
          show_online: (editItem as any).show_online !== false,
          rental_enabled: editItem.rental_enabled === true,
          rental_price: editItem.rental_price_cents ? (editItem.rental_price_cents / 100).toString() : '',
          rental_interval: editItem.rental_interval === 'week' ? 'week' : 'month',
          rentalAgreement: editItem.rental_enabled
            ? (editItem.rental_agreement?.trim() || defaultRentalAgreement(selectedBusiness?.name))
            : (editItem.rental_agreement || ''),
          restock_returns: editItem.restock_returns === true
        });
        setSelectedCategory(editItem.category_id || null);
        setSelectedSupplier(editItem.supplier_id || null);
        setImages(editPhotoUrl ? [{ id: '', url: editPhotoUrl, preview: editPhotoUrl }] : []);
        setHasVariants(false);
        setProductOptions([]);
        setVariants([]);
        setAddingTax(false);
        setTaxName('');
        setTaxRateInput('');
        void getStockItemAdditionalTaxes(editItem.id).then(setExtraTaxes).catch(() => setExtraTaxes([]));
      } else if (isOneTime) {
        setExtraTaxes([]);
        // Pre-fill form if props are provided
        setFormData(prev => ({
          ...prev,
          is_one_time: true
        }));
      } else {
        setExtraTaxes([]);
      }
    } else {
      // Reset form when modal closes
      setFormData({
        name: '',
        description: '',
        acquisition_cost: '0',
        price: '0',
        deposit: '',
        depositLabel: '',
        sku: '',
        unit: 'unit',
        quantity: '0',
        reorder_point: '0',
        max_quantity: '100',
        is_one_time: false,
        track_inventory: true,
        allow_oversell: false,
        tax_exempt: false,
        show_online: true,
        rental_enabled: false,
        rental_price: '',
        rental_interval: 'month',
        rentalAgreement: '',
        restock_returns: false
      });
      setSelectedCategory(null);
      setSelectedSupplier(null);
      setImages([]);
      setIsMediaPickerOpen(false);
      setHasVariants(false);
      setProductOptions([]);
      setVariants([]);
      setExtraTaxes([]);
      setAddingTax(false);
      setTaxName('');
      setTaxRateInput('');
    }
  }, [isOpen, isOneTime, editItem, editPhotoUrl]);

  // Pre-select supplier when suppliers are loaded and preselectedSupplierId is provided
  useEffect(() => {
    if (isOpen && preselectedSupplierId && suppliers.length > 0) {
      const supplier = suppliers.find(s => s.id === preselectedSupplierId);
      if (supplier) {
        setSelectedSupplier(supplier.id);
      }
    }
  }, [isOpen, preselectedSupplierId, suppliers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  const loadCategories = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getStockCategories(selectedBusiness.id);
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    }
  };

  const loadSuppliers = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    }
  };

  // Get the currently selected supplier details
  const getSelectedSupplierDetails = () => {
    if (!selectedSupplier) return null;
    return suppliers.find(supplier => supplier.id === selectedSupplier);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        name: '',
        acquisition_cost: '',
        price: '',
        deposit: '',
        description: '',
        sku: '',
        unit: 'unit',
        quantity: '0',
        reorder_point: '0',
        max_quantity: '100',
        is_one_time: false,
        track_inventory: true,
        allow_oversell: false,
        tax_exempt: false,
        show_online: true,
        rental_enabled: false,
        rental_price: '',
        rental_interval: 'month',
        rentalAgreement: '',
        restock_returns: false
      });
      setSelectedCategory(null);
      setIsAddingNewCategory(false);
      setNewCategoryName('');
      setIsCategoryDropdownOpen(false);
      setSelectedSupplier(null);
      setIsAddingSupplier(false);
      setIsSelectingSupplier(false);
      setSupplierFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
      setImages([]);
      setHasVariants(false);
      setProductOptions([]);
      setVariants([]);
      setError(null);
    }
  };

  const handleAddCategory = async () => {
    if (!selectedBusiness?.id || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createStockCategory({
        business_id: selectedBusiness.id,
        name: newCategoryName,
        description: null,
        order_index: categories.length
      });

      await loadCategories();
      setSelectedCategory(newCategory.id);
      setIsAddingNewCategory(false);
      setNewCategoryName('');
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);

      const supplier = await createSupplier({
        business_id: selectedBusiness.id,
        type: 'person',
        first_name: supplierFormData.first_name,
        last_name: supplierFormData.last_name,
        email: supplierFormData.email || null,
        phone: supplierFormData.phone || null,
        address: null,
        source: null,
        tags: [],
        last_interaction: null,
        lifetime_value: 0,
        next_service_date: null,
        referral_source: null,
        preferences: {},
        status: 'active',
        amount_spent: 0,
        credit_balance: 0,
        notes: null,
        tax_exempt: false,
        workflow_exempt: false,
        carfax_exempt: false,
        receive_reminders: false,
        receive_offers: false,
        lat: null,
        lng: null,
        repeat: false
      });

      await loadSuppliers();
      setSelectedSupplier(supplier.id);
      setIsAddingSupplier(false);
      setSupplierFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  };

  // Soft-delete (archive): the item disappears from inventory, sale screens,
  // and the online store, but past sales, invoices, and stock history keep
  // their records intact.
  const handleDeleteItem = async () => {
    if (!editItem) return;
    if (!confirm(`Delete "${editItem.name}"?\n\nIt will be removed from your inventory, sale screens, and online store. Past sales, invoices, and stock history are kept.`)) return;
    setDeleting(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase
        .from('stock_items')
        .update({ is_deleted: true })
        .eq('id', editItem.id);
      if (deleteError) throw deleteError;
      onSuccess();
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness?.id) {
      setError('No business selected');
      return;
    }

    if (formData.rental_enabled && !(parseFloat(formData.rental_price) > 0)) {
      setError('Set a rental rate before saving a rental product');
      return;
    }

    if (!selectedCategory && !isAddingNewCategory) {
      setError('Please select or create a category');
      return;
    }

    let categoryId = selectedCategory;

    // Create new category if needed
    if (isAddingNewCategory && newCategoryName) {
      try {
        const newCategory = await createStockCategory({
          business_id: selectedBusiness.id,
          name: newCategoryName,
          description: null,
          order_index: categories.length
        });
        categoryId = newCategory.id;
      } catch (err) {
        console.error('Error creating category:', err);
        setError(err instanceof Error ? err.message : 'Failed to create category');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // ===== Edit mode + variants: convert this simple item into a variant
      // group. The item becomes the first variant (keeping its id, stock, and
      // history); the other combinations are created as new items. =====
      if (isEditMode && editItem && hasVariants && !editItem.product_group_id) {
        const enabledVariants = variants.filter(v => v.enabled);
        if (enabledVariants.length === 0) {
          throw new Error('Add at least one variant (an option with values).');
        }

        const supplierDetails = getSelectedSupplierDetails();
        const vendorName = supplierDetails
          ? `${supplierDetails.first_name || ''} ${supplierDetails.last_name || ''}`.trim() || null
          : null;

        await convertItemToVariantGroup({
          existingItem: editItem,
          business_id: selectedBusiness.id,
          title: formData.name,
          description: formData.description || null,
          category_id: categoryId,
          supplier_id: selectedSupplier || null,
          vendor: vendorName,
          unit: formData.unit,
          options: productOptions,
          images: images.map(i => ({ url: i.url, mediaId: i.id })),
          variants: enabledVariants.map(v => ({
            option_values: v.optionValues,
            sku: v.sku || null,
            barcode: v.barcode || null,
            price: parseFloat(v.price) || 0,
            acquisition_cost: parseFloat(v.cost) || 0,
            quantity: parseInt(v.quantity) || 0,
            image_index: v.imageIndex,
          })),
        });

        onSuccess();
        return;
      }

      // ===== Edit mode: update the existing item in place =====
      if (isEditMode && editItem) {
        const isOT = formData.is_one_time || false;
        const tracksInventory = formData.track_inventory !== false;
        const maxQuantity = (isOT || !tracksInventory) ? null : (formData.max_quantity ? parseInt(formData.max_quantity) : null);
        const reorderPoint = (isOT || !tracksInventory) ? null : (formData.reorder_point ? parseInt(formData.reorder_point) : null);

        if (!isOT && tracksInventory) {
          if (maxQuantity === null || reorderPoint === null || isNaN(maxQuantity) || isNaN(reorderPoint) || maxQuantity <= reorderPoint) {
            throw new Error('Maximum quantity must be greater than reorder point');
          }
        }

        const { error: updateError } = await supabase
          .from('stock_items')
          .update({
            name: formData.name,
            description: formData.description || null,
            sku: formData.sku || null,
            unit: formData.unit,
            price: parseFloat(formData.price),
            acquisition_cost: parseFloat(formData.acquisition_cost),
            reorder_point: reorderPoint,
            max_quantity: maxQuantity,
            category_id: categoryId,
            supplier_id: selectedSupplier,
            is_one_time: isOT,
            track_inventory: tracksInventory,
            allow_oversell: tracksInventory && formData.allow_oversell === true,
            tax_exempt: formData.tax_exempt === true,
            show_online: formData.show_online !== false,
            deposit_cents: Math.max(0, Math.round((parseFloat(formData.deposit) || 0) * 100)),
            deposit_label: formData.depositLabel.trim() || null,
            restock_returns: formData.restock_returns === true,
            rental_enabled: formData.rental_enabled === true,
            rental_price_cents: Math.max(0, Math.round((parseFloat(formData.rental_price) || 0) * 100)),
            rental_interval: formData.rental_interval === 'week' ? 'week' : 'month',
            rental_agreement: formData.rental_enabled ? (formData.rentalAgreement.trim() || null) : null
          })
          .eq('id', editItem.id);

        if (updateError) throw updateError;

        // Quantity changes must flow through the stock ledger (the DB now
        // rejects direct quantity writes). The RPC records a signed adjustment
        // so the change shows up in transaction history. Untracked items have
        // no stock level, so there is nothing to set.
        // (Runs after the update above so newly re-tracked items pass the RPC's
        // tracked-only check.)
        const newQuantity = parseInt(formData.quantity);
        if (tracksInventory && !isNaN(newQuantity) && newQuantity !== editItem.quantity) {
          const { error: qtyError } = await supabase.rpc('set_stock_quantity_absolute', {
            p_item_id: editItem.id,
            p_quantity: newQuantity,
            p_note: 'Manual correction from item editor',
          });
          if (qtyError) throw qtyError;
        }

        // Sync the product photo with the selected library media
        const newUrl = images[0]?.url || null;
        const newMediaId = images[0]?.id || null;
        if (newUrl !== (editPhotoUrl || null)) {
          try {
            if (newUrl) {
              await ProductPhotoService.upsertPhoto(editItem.id, newUrl, newMediaId);
            } else {
              await ProductPhotoService.deletePhoto(editItem.id);
            }
          } catch (photoErr) {
            console.error('Error updating product photo:', photoErr);
          }
        }

        onSuccess();
        return;
      }

      // ===== Product with variants (options like Size / Color) =====
      if (hasVariants) {
        const enabledVariants = variants.filter(v => v.enabled);
        if (enabledVariants.length === 0) {
          throw new Error('Add at least one variant (an option with values).');
        }

        const supplierDetails = getSelectedSupplierDetails();
        const vendorName = supplierDetails
          ? `${supplierDetails.first_name || ''} ${supplierDetails.last_name || ''}`.trim() || null
          : null;

        await createProductGroupWithVariants({
          business_id: selectedBusiness.id,
          title: formData.name,
          description: formData.description || null,
          category_id: categoryId,
          supplier_id: selectedSupplier || null,
          vendor: vendorName,
          unit: formData.unit,
          status: 'active',
          options: productOptions,
          // Gallery references existing media-library items; each variant can
          // reference an image by index.
          images: images.map(i => ({ url: i.url, mediaId: i.id })),
          variants: enabledVariants.map(v => ({
            option_values: v.optionValues,
            sku: v.sku || null,
            barcode: v.barcode || null,
            price: parseFloat(v.price) || 0,
            acquisition_cost: parseFloat(v.cost) || 0,
            quantity: parseInt(v.quantity) || 0,
            image_index: v.imageIndex,
          })),
        });

        onSuccess();
        return;
      }

      // ===== Simple item (no options) — unchanged behavior =====
      // For one-time and untracked items, reorder_point and max_quantity are null
      const isOneTime = formData.is_one_time || false;
      const tracksInventory = formData.track_inventory !== false;
      let maxQuantity: number | null = null;
      let reorderPoint: number | null = null;
      
      if (!isOneTime && tracksInventory) {
        maxQuantity = parseInt(formData.max_quantity);
        reorderPoint = parseInt(formData.reorder_point);
        
        // Validate max quantity and reorder point for non-one-time items
        if (maxQuantity === null || reorderPoint === null || maxQuantity <= reorderPoint || maxQuantity < 1) {
          throw new Error('Maximum quantity must be greater than reorder point');
        }
      }

      const createdItem = await createStockItem({
        business_id: selectedBusiness.id,
        name: formData.name,
        acquisition_cost: parseFloat(formData.acquisition_cost) || 0,
        price: parseFloat(formData.price) || 0,
        description: formData.description || null,
        category_id: categoryId,
        supplier_id: selectedSupplier || null,
        unit: formData.unit,
        sku: formData.sku || null,
        quantity: tracksInventory ? (parseInt(formData.quantity) || 0) : 0,
        reorder_point: reorderPoint ?? 0, // Nulled in createStockItem for one-time/untracked items
        max_quantity: maxQuantity ?? 100, // Nulled in createStockItem for one-time/untracked items
        is_sellable: false,
        tax_rate: 0,
        tax_exempt: formData.tax_exempt === true,
        is_one_time: isOneTime,
        track_inventory: tracksInventory,
        allow_oversell: tracksInventory && formData.allow_oversell === true,
        show_online: formData.show_online !== false,
        deposit_cents: Math.max(0, Math.round((parseFloat(formData.deposit) || 0) * 100)),
        deposit_label: formData.depositLabel.trim() || null,
        restock_returns: formData.restock_returns === true,
        rental_enabled: formData.rental_enabled === true,
        rental_price_cents: Math.max(0, Math.round((parseFloat(formData.rental_price) || 0) * 100)),
        rental_interval: formData.rental_interval === 'week' ? 'week' : 'month',
        rental_agreement: formData.rental_enabled ? (formData.rentalAgreement.trim() || null) : null
      });

      if (createdItem?.id && extraTaxes.length > 0) {
        await Promise.all(extraTaxes.filter(t => !t.id).map(t => createStockItemAdditionalTax(createdItem.id, t.name, Number(t.rate))));
      }

      // Attach the selected library media (photo) if one was chosen during creation
      if (images[0]?.url && createdItem?.id) {
        try {
          await ProductPhotoService.upsertPhoto(createdItem.id, images[0].url, images[0].id);
        } catch (photoErr) {
          // Don't block item creation if the photo link fails
          console.error('Error attaching product photo:', photoErr);
        }
      }

      // Call onItemCreated callback if provided
      if (onItemCreated) {
        onItemCreated(createdItem.id);
      }

      onSuccess();
    } catch (err) {
      console.error('Error creating stock item:', err);
      setError(err instanceof Error ? err.message : 'Failed to create stock item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100070, // Higher than CreateOrderModal (100020)
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[920px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100080 // Higher than CreateOrderModal (100060)
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 relative">
            <h2 className="text-lg font-semibold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              {isEditMode ? 'Edit Item' : 'New Stock Item'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEditMode ? 'Update this item’s details' : 'Add a product to your inventory'}
            </p>
          </div>

          {/* Content Section */}
          <form 
            id="stock-item-form" 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-gray-50"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-5">
                  <div className="flex items-center">
                    <span>{error}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* ===== MAIN COLUMN ===== */}
                <div className="lg:col-span-2 space-y-5">
                  {/* Product details: Title + Description */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4">
                    {/* Item Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        placeholder="Short sleeve t-shirt"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                        style={{ fontSize: '16px' }}
                        placeholder="Enter item description"
                      />
                    </div>
                  </div>

                  {/* Media */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center">
                      <ImageIcon className="h-4 w-4 mr-2 text-gray-400" />
                      Media
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Square images (1:1, at least 800×800px) look best — they're shown edge-to-edge on your online store and embeds.
                    </p>
                    {images.length > 0 ? (
                      <div className="flex flex-wrap gap-3">
                        {images.map((img, index) => (
                          <div key={img.id || index} className="relative">
                            <img
                              src={img.preview}
                              alt={`Product preview ${index + 1}`}
                              className="h-24 w-24 object-cover rounded-xl border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:text-red-600 active:scale-95 transition-all duration-150 touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              aria-label="Remove image"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        {/* Select-from-library tile */}
                        <button
                          type="button"
                          onClick={() => setIsMediaPickerOpen(true)}
                          className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-blue-400 hover:bg-blue-50/40 active:scale-95 transition-all duration-150 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          aria-label={hasVariants ? 'Add media' : 'Change media'}
                        >
                          <Upload className="h-5 w-5 mb-1" />
                          <span className="text-xs font-medium">{hasVariants ? 'Add' : 'Change'}</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsMediaPickerOpen(true)}
                        className="w-full border-2 border-dashed border-gray-300 rounded-xl py-8 px-4 flex flex-col items-center justify-center text-center hover:border-blue-400 hover:bg-blue-50/40 active:scale-[0.99] transition-all duration-150 touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 shadow-sm flex items-center mb-2">
                          <ImageIcon className="h-4 w-4 mr-2" />
                          Add media
                        </div>
                        <p className="text-xs text-gray-500">
                          {hasVariants ? 'Select from your media library — add multiple' : 'Select from your media library'}
                        </p>
                      </button>
                    )}
                  </div>

                  {/* Simple item: Pricing + Inventory (hidden when product has variants) */}
                  {!hasVariants && (
                  <>
                  {/* Pricing */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Pricing
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Item Cost */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Item Cost
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.acquisition_cost}
                            onChange={(e) => {
                              const cost = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                acquisition_cost: cost,
                                price: cost // Auto-populate price with cost
                              }));
                            }}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      {/* Retail Price */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Retail Price
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <TrendingUp className="h-4 w-4 text-green-400" />
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.price}
                            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} 
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Container deposit — refundable, tracked per customer, never taxed */}
                    <div className="pt-4 mt-4 border-t border-gray-100">
                      <label className="block text-sm font-semibold text-gray-700">
                        Container deposit
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5 mb-2">
                        Refundable deposit charged per unit delivered and refunded when containers come back. Tracked per customer. Never taxed. Leave the amount blank for none.
                      </p>
                      <div className="relative max-w-[200px]">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.deposit}
                          onChange={(e) => setFormData(prev => ({ ...prev, deposit: e.target.value }))}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                          placeholder="0.00"
                        />
                      </div>
                      {(parseFloat(formData.deposit) || 0) > 0 && (
                        <div className="mt-3 max-w-sm">
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Label <span className="font-normal text-gray-400">(optional)</span>
                          </label>
                          <input
                            type="text"
                            maxLength={40}
                            value={formData.depositLabel}
                            onChange={(e) => setFormData(prev => ({ ...prev, depositLabel: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="bottle"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Shown as “{formData.depositLabel.trim()
                              ? `${formData.depositLabel.trim().charAt(0).toUpperCase()}${formData.depositLabel.trim().slice(1)} deposit`
                              : 'Deposit'}”. Leave blank for just “Deposit”.
                          </p>
                        </div>
                      )}

                      {/* Returned containers can replenish stock (not for rentals —
                          rental equipment restocks when the rental ends). */}
                      {(parseFloat(formData.deposit) || 0) > 0 && !formData.rental_enabled && (
                        <div className="flex items-center justify-between mt-3">
                          <div className="pr-3">
                            <p className="text-sm font-semibold text-gray-700">Returned containers restock this item</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Empties collected at delivery or pickup go straight back into your stock count as sellable. Lost containers never restock. Leave off if empties need refilling first.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, restock_returns: !prev.restock_returns }))}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                              formData.restock_returns ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-pressed={formData.restock_returns}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                formData.restock_returns ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Rental — recurring billing agreement instead of one-time sale */}
                    <div className="pt-4 mt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="pr-3">
                          <p className="text-sm font-semibold text-gray-700">Rent this product</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Customers rent instead of buy. Checkout starts a recurring agreement billed automatically at this rate until canceled. Units out on rent come from your inventory count.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => {
                            const turningOn = !prev.rental_enabled;
                            return {
                              ...prev,
                              rental_enabled: turningOn,
                              rentalAgreement: turningOn && !prev.rentalAgreement.trim()
                                ? defaultRentalAgreement(selectedBusiness?.name)
                                : prev.rentalAgreement,
                            };
                          })}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            formData.rental_enabled ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          aria-pressed={formData.rental_enabled}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              formData.rental_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      {formData.rental_enabled && (
                        <div className="mt-3 flex items-end gap-3">
                          <div className="flex-1 max-w-[200px]">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Rental rate <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.rental_price}
                                onChange={(e) => setFormData(prev => ({ ...prev, rental_price: e.target.value }))}
                                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                style={{ fontSize: '16px' }}
                                placeholder="14.99"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Billed every</label>
                            <select
                              value={formData.rental_interval}
                              onChange={(e) => setFormData(prev => ({ ...prev, rental_interval: e.target.value as 'month' | 'week' }))}
                              className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 bg-white"
                              style={{ fontSize: '16px' }}
                            >
                              <option value="month">Month</option>
                              <option value="week">Week</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {formData.rental_enabled && (
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Rental agreement
                          </label>
                          <textarea
                            value={formData.rentalAgreement}
                            onChange={(e) => setFormData(prev => ({ ...prev, rentalAgreement: e.target.value }))}
                            rows={6}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 leading-relaxed"
                            style={{ fontSize: '16px' }}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Customers confirm this before their card is charged. Edit it to match how this rental works.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Charge tax — off marks the product tax-exempt on invoices */}
                    <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
                      <div className="pr-3">
                        <p className="text-sm font-semibold text-gray-700">Charge tax</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Applies your business tax rates when this product is sold. Turn off for tax-exempt products.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, tax_exempt: !prev.tax_exempt }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                          !formData.tax_exempt ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        aria-pressed={!formData.tax_exempt}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            !formData.tax_exempt ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {!formData.tax_exempt && (
                      <div className="pt-3 mt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Additional tax rates</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Added on top of your business tax rates, only for this product.
                            </p>
                          </div>
                          {!addingTax && (
                            <button
                              type="button"
                              onClick={() => setAddingTax(true)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200"
                            >
                              <Plus className="h-4 w-4 text-gray-600" />
                            </button>
                          )}
                        </div>
                        {extraTaxes.length === 0 && !addingTax && (
                          <p className="text-xs text-gray-400 mt-2">No additional tax rates. Business tax rates apply.</p>
                        )}
                        {extraTaxes.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {extraTaxes.map((tax, index) => (
                              <div key={tax.id || `${tax.name}-${tax.rate}-${index}`} className="flex items-center justify-between rounded-xl bg-gray-50 border border-gray-200/70 px-3 py-2">
                                <span className="text-sm text-gray-900">{tax.name} <span className="text-gray-500">({tax.rate}%)</span></span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setExtraTaxes(prev => prev.filter((_, i) => i !== index));
                                    if (tax.id) void deleteStockItemAdditionalTax(tax.id).catch(() => {
                                      if (editItem) void getStockItemAdditionalTaxes(editItem.id).then(setExtraTaxes);
                                    });
                                  }}
                                  className="text-gray-400 hover:text-red-600"
                                  aria-label={`Remove ${tax.name}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {addingTax && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <input
                              value={taxName}
                              onChange={(e) => setTaxName(e.target.value)}
                              placeholder="e.g. Environmental levy"
                              className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              style={{ fontSize: '16px' }}
                              autoFocus
                            />
                            <input
                              value={taxRateInput}
                              onChange={(e) => setTaxRateInput(e.target.value)}
                              placeholder="Rate %"
                              inputMode="decimal"
                              className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              style={{ fontSize: '16px' }}
                            />
                            <div className="col-span-2 flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const rate = parseFloat(taxRateInput);
                                  if (!taxName.trim() || !(rate > 0) || rate > 100) return;
                                  const name = taxName.trim();
                                  if (isEditMode && editItem) {
                                    void createStockItemAdditionalTax(editItem.id, name, rate).then(row => {
                                      setExtraTaxes(prev => [...prev, row]);
                                    }).catch(err => setError(err?.message || 'Failed to add tax rate'));
                                  } else {
                                    setExtraTaxes(prev => [...prev, { id: '', stock_item_id: '', name, rate, created_at: '' }]);
                                  }
                                  setTaxName('');
                                  setTaxRateInput('');
                                  setAddingTax(false);
                                }}
                                className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
                                disabled={!taxName.trim() || !taxRateInput}
                              >
                                Add
                              </button>
                              <button
                                type="button"
                                onClick={() => { setAddingTax(false); setTaxName(''); setTaxRateInput(''); }}
                                className="px-3 py-2 rounded-xl bg-gray-100 text-sm font-semibold text-gray-600"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show in online store / website embeds */}
                    <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
                      <div className="pr-3">
                        <p className="text-sm font-semibold text-gray-700">Show in online store</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Makes this product visible on your public store page and website embeds.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, show_online: !prev.show_online }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                          formData.show_online ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        aria-pressed={formData.show_online}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            formData.show_online ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Inventory */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Inventory
                    </h3>
                    <div className="space-y-4">
                      {/* SKU */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          SKU (Optional)
                        </label>
                        <div className="relative">
                          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={formData.sku}
                            onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="Enter SKU code"
                          />
                        </div>
                      </div>

                      {/* Track stock levels — off for made-to-order items (e.g.
                          produced per delivery). Sales still log movements. */}
                      <div className="flex items-center justify-between py-1">
                        <div className="pr-3">
                          <p className="text-sm font-semibold text-gray-700">Track stock levels</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Turn off for made-to-order items — they stay sellable without stock counts, and sales are still logged in history.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, track_inventory: !prev.track_inventory }))}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            formData.track_inventory ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          aria-pressed={formData.track_inventory}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              formData.track_inventory ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Continue selling past zero (backorders) — only
                          meaningful when stock levels are tracked. */}
                      {formData.track_inventory && (
                        <div className="flex items-center justify-between py-1">
                          <div className="pr-3">
                            <p className="text-sm font-semibold text-gray-700">Continue selling when out of stock</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Customers can keep purchasing at zero stock — the count goes negative so you can see how many are backordered.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, allow_oversell: !prev.allow_oversell }))}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                              formData.allow_oversell ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-pressed={formData.allow_oversell}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                formData.allow_oversell ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      )}

                      {formData.track_inventory && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Current Stock */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Current Stock *
                          </label>
                          <input
                            type="number"
                            required
                            min="0"
                            value={formData.quantity}
                            onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0"
                          />
                        </div>

                        {/* Reorder Point - Hidden for one-time items */}
                        {!formData.is_one_time && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Reorder Point *
                            </label>
                            <input
                              type="number"
                              required
                              min="0"
                              value={formData.reorder_point}
                              onChange={(e) => setFormData(prev => ({ ...prev, reorder_point: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                              placeholder="0"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Alert when stock falls below this level
                            </p>
                          </div>
                        )}

                        {/* Maximum Stock - Hidden for one-time items */}
                        {!formData.is_one_time && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Maximum Stock *
                            </label>
                            <input
                              type="number"
                              required
                              min="1"
                              value={formData.max_quantity}
                              onChange={(e) => setFormData(prev => ({ ...prev, max_quantity: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                              placeholder="100"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Maximum quantity for auto-ordering
                            </p>
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  </div>
                  </>
                  )}

                  {/* Product with variants (options like Size / Color) */}
                  {hasVariants && (
                    <VariantsEditor
                      options={productOptions}
                      setOptions={setProductOptions}
                      variants={variants}
                      setVariants={setVariants}
                      defaultPrice={formData.price}
                      defaultCost={formData.acquisition_cost}
                      gallery={images}
                    />
                  )}
                </div>

                {/* ===== SIDEBAR COLUMN ===== */}
                <div className="space-y-5">
                  {/* Product organization */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Product organization
                    </h3>

                    {/* Category Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Category *
                      </label>
                      {!isAddingNewCategory ? (
                        <div className="relative" ref={categoryDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 bg-white text-left flex items-center justify-between"
                            style={{ fontSize: '16px' }}
                          >
                            <span className={selectedCategory ? 'text-gray-900' : 'text-gray-400'}>
                              {selectedCategory 
                                ? categories.find(c => c.id === selectedCategory)?.name || 'Select a category...'
                                : 'Select a category...'
                              }
                            </span>
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {isCategoryDropdownOpen && (
                            <div 
                              className="absolute left-0 right-0 mt-2 max-h-48 overflow-y-auto bg-white rounded-xl shadow-lg border border-gray-200/60"
                              style={{ zIndex: 100100 }}
                            >
                              {categories.map(category => (
                                <button
                                  key={category.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCategory(category.id);
                                    setIsCategoryDropdownOpen(false);
                                  }}
                                  className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors"
                                >
                                  {category.name}
                                </button>
                              ))}
                              <div className="border-t border-gray-200">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsAddingNewCategory(true);
                                    setIsCategoryDropdownOpen(false);
                                    setNewCategoryName('');
                                  }}
                                  className="w-full px-4 py-3 text-left text-sm text-blue-600 hover:bg-blue-50 font-semibold flex items-center"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  + Add category
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="p-3 bg-blue-50 rounded-xl border border-blue-200/60">
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              placeholder="Enter category name"
                              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                              autoFocus
                            />
                            <div className="mt-2 flex justify-end space-x-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsAddingNewCategory(false);
                                  setNewCategoryName('');
                                  setSelectedCategory(null);
                                }}
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleAddCategory}
                                disabled={!newCategoryName.trim() || loading}
                                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                {loading ? 'Creating...' : 'Create Category'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Vendor / Supplier */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Vendor
                      </label>
                      <div className="border border-gray-200/60 rounded-xl p-3 bg-gray-50">
                        {getSelectedSupplierDetails() ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center min-w-0">
                              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center mr-3 shadow-sm flex-shrink-0">
                                <Building2 className="h-4 w-4 text-white" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-bold text-gray-900 truncate">
                                  {getSelectedSupplierDetails()?.first_name} {getSelectedSupplierDetails()?.last_name}
                                </div>
                                {getSelectedSupplierDetails()?.email && (
                                  <div className="text-xs text-blue-600 truncate">{getSelectedSupplierDetails()?.email}</div>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setIsSelectingSupplier(true)}
                              className="ml-2 px-3 py-1.5 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation flex-shrink-0"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Change
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center min-w-0">
                              <div className="h-9 w-9 rounded-xl bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                                <Building2 className="h-4 w-4 text-gray-400" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-bold text-gray-900">No vendor</div>
                                <div className="text-xs text-gray-500">No supplier assigned</div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setIsSelectingSupplier(true)}
                              className="ml-2 px-3 py-1.5 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation flex-shrink-0"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Select
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Options
                    </h3>

                    {/* Variants toggle. Available when creating, and when editing a
                        simple item (converts it into a variant group — the item becomes
                        the first variant and keeps its stock and history). Items already
                        in a group are edited from the product view instead. */}
                    {(!isEditMode || !editItem?.product_group_id) && (
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="has_variants"
                        checked={hasVariants}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setHasVariants(checked);
                          if (checked) {
                            // One-time is incompatible with variants
                            setFormData(prev => ({ ...prev, is_one_time: false }));
                            setProductOptions(prev => (prev.length === 0 ? [{ name: '', values: [] }] : prev));
                          } else {
                            setProductOptions([]);
                            setVariants([]);
                          }
                        }}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <label htmlFor="has_variants" className="text-sm font-semibold text-gray-700 cursor-pointer">
                          This product has options like size or color
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          {isEditMode
                            ? 'Create multiple variants, each with its own price, SKU and stock. This item becomes the first variant and keeps its stock and history.'
                            : 'Create multiple variants, each with its own price, SKU and stock.'}
                        </p>
                      </div>
                    </div>
                    )}

                    {/* One-time item (not applicable to products with variants) */}
                    {!hasVariants && (
                      <div className="flex items-start gap-3 pt-3 border-t border-gray-100">
                        <input
                          type="checkbox"
                          id="is_one_time"
                          checked={formData.is_one_time || false}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setFormData(prev => ({
                              ...prev,
                              is_one_time: isChecked,
                              // Clear reorder_point and max_quantity when checking one-time
                              reorder_point: isChecked ? '' : (prev.reorder_point || '0'),
                              max_quantity: isChecked ? '' : (prev.max_quantity || '100')
                            }));
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <label htmlFor="is_one_time" className="text-sm font-semibold text-gray-700 cursor-pointer">
                            One-time item
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            This item will be automatically hidden from inventory once it's fully withdrawn (quantity reaches 0)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
          
          {/* Footer */}
          <div className={`bg-white border-t border-gray-200 ${isMobile ? 'p-4' : 'px-6 py-4'}`}>
            <button
              type="submit"
              form="stock-item-form"
              disabled={loading}
              className="w-full px-4 py-3 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {!isEditMode && <Plus className="h-5 w-5 mr-2" />}
                  {isEditMode ? 'Save Changes' : hasVariants ? 'Create Product' : 'Create Item'}
                </div>
              )}
            </button>
            {isEditMode && (
              <button
                type="button"
                disabled={loading || deleting}
                onClick={handleDeleteItem}
                className="w-full mt-2 px-4 py-2.5 text-sm font-semibold text-red-600 rounded-xl hover:bg-red-50 active:bg-red-100 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation flex items-center justify-center gap-1.5"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? 'Deleting…' : 'Delete Item'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Supplier Selection Modal */}
      {isSelectingSupplier && (
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity duration-200"
          style={{
            zIndex: 100090,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={() => setIsSelectingSupplier(false)}
        >
          <div 
            className={`bg-white transform transition-all duration-200 ${
              isMobile 
                ? `fixed inset-0 w-full`
                : `fixed inset-y-0 right-0 w-full md:w-[500px] shadow-2xl`
            }`}
            style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100100,
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="h-full flex flex-col overflow-hidden">
              {/* Header */}
              <div className={`bg-gray-50 border-b border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between sticky top-0 z-10`}>
                <div>
                  <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900`} style={{ letterSpacing: '-0.02em' }}>
                    Select Supplier
                  </h2>
                  {!isMobile && <p className="text-sm text-gray-500 mt-0.5">Choose or add a supplier</p>}
                </div>
                <button
                  onClick={() => setIsSelectingSupplier(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div 
                className={`flex-1 overflow-y-auto bg-white ${isMobile ? 'p-4' : 'p-6'}`}
                style={{ 
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div className="space-y-3">
                  {/* No Supplier Option */}
                  <div
                    className={`bg-white rounded-[12px] border p-4 cursor-pointer active:scale-[0.98] transition-all duration-150 touch-manipulation ${
                      selectedSupplier === null ? 'border-blue-500 bg-blue-50' : 'border-gray-200/60'
                    } shadow-[0_1px_2px_rgba(0,0,0,0.05)]`}
                    onClick={() => {
                      setSelectedSupplier(null);
                      setIsSelectingSupplier(false);
                    }}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-center">
                      <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-400 flex items-center justify-center mr-3 flex-shrink-0">
                        <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">No Supplier</div>
                        <div className="text-xs text-gray-500">Don't assign a supplier</div>
                      </div>
                    </div>
                  </div>

                  {/* Existing Suppliers */}
                  {suppliers.map((supplier) => (
                    <div
                      key={supplier.id}
                      className={`bg-white rounded-[12px] border p-4 cursor-pointer active:scale-[0.98] transition-all duration-150 touch-manipulation ${
                        selectedSupplier === supplier.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200/60'
                      } shadow-[0_1px_2px_rgba(0,0,0,0.05)]`}
                      onClick={() => {
                        setSelectedSupplier(supplier.id);
                        setIsSelectingSupplier(false);
                      }}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-700 flex items-center justify-center text-xs sm:text-sm font-bold text-white mr-3 flex-shrink-0">
                          {supplier.first_name?.[0]}{supplier.last_name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {supplier.first_name} {supplier.last_name}
                          </div>
                          {supplier.email && (
                            <div className="text-xs text-blue-600 truncate">{supplier.email}</div>
                          )}
                          {supplier.type === 'business' && (
                            <div className="text-xs text-blue-600 font-medium">Business Supplier</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add New Supplier */}
                  <button
                    type="button"
                    onClick={() => {
                      setIsSelectingSupplier(false);
                      setIsAddingSupplier(true);
                    }}
                    className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-xl text-sm font-semibold text-blue-600 active:text-blue-700 active:border-blue-400 active:bg-blue-50 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Supplier
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Supplier Modal */}
      {isAddingSupplier && (
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity duration-200"
          style={{
            zIndex: 100110, // Higher than NewStockItemModal content (100080)
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
          }}
          onClick={() => setIsAddingSupplier(false)}
        >
          <div 
            className={`bg-white transform transition-all duration-200 ${
              isMobile 
                ? `relative w-full h-full`
                : `fixed inset-y-0 right-0 w-full md:w-[500px] shadow-2xl`
            }`}
            style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100120 // Higher than NewStockItemModal content (100080)
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="h-full flex flex-col overflow-hidden">
              {/* Close Button */}
              <button
                onClick={() => setIsAddingSupplier(false)}
                className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>

              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
                <h2 className="text-2xl font-bold text-gray-900" style={{ 
                  letterSpacing: '-0.02em'
                }}>
                  Add Supplier
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Create a new supplier
                </p>
              </div>

              {/* Content */}
              <form 
                onSubmit={handleAddSupplier} 
                className="flex-1 overflow-y-auto bg-white p-6" 
                style={{ 
                  WebkitOverflowScrolling: 'touch',
                  paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
                }}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={supplierFormData.first_name}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter first name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={supplierFormData.last_name}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter last name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={supplierFormData.email}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      value={supplierFormData.phone}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsAddingSupplier(false)}
                      className="px-4 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {loading ? 'Adding...' : 'Add Supplier'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedBusiness && (
        <MediaLibraryPicker
          isOpen={isMediaPickerOpen}
          businessId={selectedBusiness.id}
          multiple={hasVariants}
          initialSelectedUrls={images.map(i => i.url)}
          onClose={() => setIsMediaPickerOpen(false)}
          onDone={handleMediaSelected}
        />
      )}
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}